/**
 * The game's sounds, made in code with the Web Audio API (SND-001, owner 2026-09-27):
 * no audio files, no licences, nothing to download, and nothing the strict CSP has to
 * allow.
 *
 * Modelled, not beeped. The first version used pitched oscillators, and the owner heard
 * it at once: "too high pitched and clacky, retro". A real knock is a short burst of
 * noise ringing through a few UNEVENLY spaced resonances of the object struck (modal
 * synthesis); pure tones with harmonic pitch are what makes a sound read as an old
 * console. So every table sound here is noise excitation through band-pass resonators,
 * pitched low, with a small synthetic room behind it for the pool-hall air.
 *
 * Browsers only let a page start sound after the player touches it, so the audio
 * context is made on the first press (`unlock`), and every cue before that is silently
 * skipped. Anything here failing must never stop the game: sound is decoration.
 */
import type { Cue, CueKind } from './cues.ts';

export interface SoundEngine {
  /**
   * Build the audio engine ahead of time, paused (SND-002). Call when the page is idle:
   * building it costs ~60 ms on a phone, which made the first shot stutter when it
   * happened on the tap that took the shot. Browsers allow a paused engine to be made
   * before any touch (Chrome logs a warning that it is not allowed to START yet).
   */
  prepare(): void;
  /** Call from a user gesture (pointerdown, keydown): browsers need one to start sound. */
  unlock(): void;
  play(cue: Cue): void;
  /** The player's effects volume, 0–1. 0 makes nothing at all. */
  setVolume(volume: number): void;
}

type ContextFactory = () => AudioContext | undefined;

function browserContext(): AudioContext | undefined {
  try {
    // Safari 17+: follow the ringer switch, like other game sounds on an iPhone.
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session !== undefined) session.type = 'ambient';
  } catch {
    // Older browsers have no audio session; nothing to do.
  }
  return typeof AudioContext === 'function' ? new AudioContext() : undefined;
}

/** How much of each sound goes to the room. A touch: a hall, not a cathedral. */
const ROOM_SEND = 0.22;

/**
 * The shared signal chain: voices → bus → (dry, and a small room) → limiter → speaker.
 * Built on any context, so an OfflineAudioContext can render it for measuring.
 */
export interface Chain {
  readonly bus: GainNode;
  readonly noise: AudioBuffer;
}

export function buildChain(ctx: BaseAudioContext, volume: number): Chain {
  // A compressor last, so a crowded break cannot clip.
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -12;
  limiter.ratio.value = 6;
  limiter.connect(ctx.destination);
  const bus = ctx.createGain();
  bus.gain.value = volume;
  bus.connect(limiter);
  const room = ctx.createConvolver();
  room.buffer = roomResponse(ctx);
  const send = ctx.createGain();
  send.gain.value = ROOM_SEND;
  bus.connect(send);
  send.connect(room);
  room.connect(limiter);
  return { bus, noise: whiteNoise(ctx) };
}

/** Half a second of white noise, reused by every cue. */
function whiteNoise(ctx: BaseAudioContext): AudioBuffer {
  const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate / 2), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}

/**
 * A small room's echo: 0.45 s of decaying noise, darkened as it fades (a carpeted,
 * felt-covered hall swallows the highs first). Stereo, so it opens up on headphones.
 */
function roomResponse(ctx: BaseAudioContext): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * 0.45);
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buffer.getChannelData(ch);
    let smooth = 0;
    for (let i = 0; i < length; i++) {
      const t = i / ctx.sampleRate;
      // A one-pole low-pass whose cutoff falls with time: bright early, dull late.
      const k = 0.55 - 0.45 * (i / length);
      smooth += k * (Math.random() * 2 - 1 - smooth);
      data[i] = smooth * Math.exp(-t / 0.09) * (t < 0.008 ? t / 0.008 : 1);
    }
  }
  return buffer;
}

export function createSoundEngine(
  initialVolume: number,
  createContext: ContextFactory = browserContext,
): SoundEngine {
  let volume = initialVolume;
  let ctx: AudioContext | undefined;
  let chain: Chain | undefined;
  /** A touch has asked for sound. Until then nothing plays, even if the engine exists. */
  let unlocked = false;

  function prepare(): void {
    if (ctx !== undefined) return;
    try {
      ctx = createContext();
      if (ctx !== undefined) chain = buildChain(ctx, volume);
    } catch {
      ctx = undefined;
      chain = undefined;
    }
  }

  return {
    prepare() {
      // No engine at all for a player who has turned effects off.
      if (volume > 0) prepare();
    },
    unlock() {
      unlocked = true;
      prepare();
      try {
        if (ctx?.state === 'suspended') void ctx.resume();
      } catch {
        // A refused resume just means silence.
      }
    },
    play(cue) {
      if (!unlocked || volume <= 0 || ctx === undefined || chain === undefined) return;
      // 'suspended' here means a resume is on its way: a sound scheduled now plays the
      // moment it lands, so the first shot's strike is not lost.
      if (ctx.state === 'closed') return;
      try {
        playVoice(ctx, chain, cue.kind, cue.gain, ctx.currentTime);
      } catch {
        // Never let a sound break a shot.
      }
    },
    setVolume(next) {
      volume = next > 1 ? 1 : next > 0 ? next : 0;
      if (ctx !== undefined && chain !== undefined) {
        chain.bus.gain.setTargetAtTime(volume, ctx.currentTime, 0.02);
      }
    },
  };
}

/** Start one cue's sound at time `t` on the context's clock. */
export function playVoice(
  ctx: BaseAudioContext,
  chain: Chain,
  kind: CueKind,
  gain: number,
  t: number,
): void {
  const trim = ctx.createGain();
  trim.gain.value = TRIM[kind];
  trim.connect(chain.bus);
  VOICES[kind]({ ctx, out: trim, noise: chain.noise, t, gain });
  // Let the node go once the sound has died away.
  setTimeoutSafe(() => trim.disconnect(), 3000);
}

/**
 * Output level per voice, measured: each voice rendered offline at full strength and
 * brought to a target peak (ball and cushion about 0.45, a pot 0.5, the chime 0.3, a
 * button tick 0.1). Narrow resonators pass little of a millisecond's burst, so the
 * raw voices peak at only a few percent.
 */
const TRIM: Readonly<Record<CueKind, number>> = {
  strike: 13,
  ball: 22,
  cushion: 11,
  pocket: 12,
  scratch: 14,
  cleared: 2.1,
  tick: 34,
};

function setTimeoutSafe(fn: () => void, ms: number): void {
  if (typeof setTimeout === 'function') setTimeout(fn, ms);
}

interface Voice {
  readonly ctx: BaseAudioContext;
  readonly out: AudioNode;
  readonly noise: AudioBuffer;
  /** Start time, on the context's clock. */
  readonly t: number;
  readonly gain: number;
}

/** A small random spread, so repeated contacts never sound like one sample. */
function vary(value: number, spread: number): number {
  return value * (1 + (Math.random() * 2 - 1) * spread);
}

/** A slice of noise, shaped by a near-instant attack and an exponential decay. */
function noiseBurst(v: Voice, decayS: number, delayS: number): AudioNode {
  const src = v.ctx.createBufferSource();
  src.buffer = v.noise;
  const env = v.ctx.createGain();
  const start = v.t + delayS;
  env.gain.setValueAtTime(0.0001, start);
  env.gain.exponentialRampToValueAtTime(1, start + 0.0008);
  env.gain.exponentialRampToValueAtTime(0.0001, start + decayS);
  src.connect(env);
  // A random slice of the noise, so no two excitations are identical.
  src.start(start, Math.random() * 0.4, decayS + 0.01);
  return env;
}

/**
 * Strike a body: a very short burst of noise rung through resonances at uneven
 * frequencies. `modes` is [Hz, Q, level]: Q sets how long a mode rings (higher rings
 * longer), and the uneven spacing is what makes it sound like a solid object.
 */
function knock(
  v: Voice,
  modes: readonly (readonly [number, number, number])[],
  level: number,
  excitationS = 0.003,
  delayS = 0,
): void {
  const hit = noiseBurst(v, excitationS, delayS);
  for (const [hz, q, modeLevel] of modes) {
    const f = v.ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = hz;
    f.Q.value = q;
    const g = v.ctx.createGain();
    // A narrow band-pass passes little of a short burst; Q brings it back to level.
    g.gain.value = level * modeLevel * Math.sqrt(q);
    hit.connect(f);
    f.connect(g);
    g.connect(v.out);
  }
}

/** Low, filtered noise: the dull weight of a thud, without any pitch. */
function thud(v: Voice, cutoffHz: number, level: number, decayS: number, delayS = 0): void {
  const body = noiseBurst(v, decayS, delayS);
  const f = v.ctx.createBiquadFilter();
  f.type = 'lowpass';
  f.frequency.value = cutoffHz;
  f.Q.value = 0.8;
  const g = v.ctx.createGain();
  g.gain.value = level;
  body.connect(f);
  f.connect(g);
  g.connect(v.out);
}

/**
 * The voices. Numbers are by ear, tuned with the owner; they are sound design, not
 * physics, so they live here rather than in `config.ts`.
 */
const VOICES: Readonly<Record<CueKind, (v: Voice) => void>> = {
  // Leather tip on the cue ball: a soft, dull "tuk", well below the ball click.
  strike(v) {
    knock(
      v,
      [
        [vary(1050, 0.04), 5, 1],
        [vary(1600, 0.04), 7, 0.45],
      ],
      0.55 * v.gain,
      0.004,
    );
    thud(v, 380, 0.5 * v.gain, 0.035);
  },
  // Resin ball on ball: the "clack". Low-ish and solid, with a short body under it.
  ball(v) {
    const s = vary(1, 0.05);
    knock(
      v,
      [
        [1750 * s, 22, 1],
        [2480 * s, 26, 0.6],
        [3350 * s, 18, 0.3],
        [4700 * s, 12, 0.12],
      ],
      0.5 * v.gain,
      0.0015,
    );
    thud(v, 450, 0.45 * v.gain, 0.022);
  },
  // Rubber cushion, wooden rail behind it: a muffled thump, no pitch to speak of.
  cushion(v) {
    thud(v, vary(300, 0.08), 1.1 * v.gain, 0.075);
    knock(
      v,
      [
        [vary(185, 0.05), 5, 1],
        [vary(560, 0.05), 4, 0.3],
      ],
      0.45 * v.gain,
      0.006,
    );
  },
  // The drop: a dull knock as it falls into the leather, then it rolls and settles.
  pocket(v) {
    thud(v, 260, 1.2 * v.gain, 0.1);
    knock(v, [[vary(140, 0.05), 4, 1]], 0.6 * v.gain, 0.008);
    for (const [delayS, level] of [
      [0.09, 0.4],
      [0.17, 0.28],
      [0.24, 0.18],
      [0.3, 0.1],
    ] as const) {
      knock(
        v,
        [
          [vary(720, 0.08), 8, 1],
          [vary(1150, 0.08), 10, 0.5],
        ],
        level * v.gain,
        0.003,
        delayS,
      );
      thud(v, 350, level * 0.6 * v.gain, 0.03, delayS);
    }
  },
  // The white goes in: the same drop, deeper, with a heavier landing.
  scratch(v) {
    thud(v, 200, 1.3 * v.gain, 0.13);
    knock(v, [[vary(110, 0.05), 4, 1]], 0.7 * v.gain, 0.01);
    for (const [delayS, level] of [
      [0.1, 0.35],
      [0.19, 0.22],
      [0.27, 0.12],
    ] as const) {
      knock(v, [[vary(600, 0.08), 8, 1]], level * v.gain, 0.003, delayS);
    }
  },
  // Table cleared: a soft, warm chord that swells in rather than pings.
  cleared(v) {
    for (const [i, hz] of [523.25, 659.25, 783.99, 1046.5].entries()) {
      const osc = v.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = hz;
      const env = v.ctx.createGain();
      const start = v.t + i * 0.11;
      env.gain.setValueAtTime(0.0001, start);
      env.gain.exponentialRampToValueAtTime(0.07 * v.gain, start + 0.04);
      env.gain.exponentialRampToValueAtTime(0.0001, start + 1.4);
      osc.connect(env);
      env.connect(v.out);
      osc.start(start);
      osc.stop(start + 1.45);
    }
  },
  // A button: a faint wooden tap.
  tick(v) {
    knock(v, [[vary(1300, 0.03), 6, 1]], 0.12 * v.gain, 0.002);
  },
};
